/**
 * Serviço para gerenciar contexto do usuário (agência vs cliente)
 * Determina as permissões e restrições de navegação
 * 
 * IMPORTANTE: Agência tem prioridade sobre cliente
 * Se usuário está autenticado via Firebase (agência), sessão de cliente é ignorada
 */
class UserContextService {
  constructor() {
    this.userType = 'unknown'; // 'agency', 'client', 'unknown'
    this.clientContext = null;
    this.listeners = new Set();
    this.forceClientMode = false; // Flag para forçar modo cliente quando agência visita perfil
    this.impersonationContext = null; // {agencyId, agencyName, agencyEmail} - admin impersonation
  }

  /**
   * Detecta o tipo de usuário baseado na sessão
   * PRIORIDADE: Agência (Firebase) > Cliente (sessionStorage)
   */
  detectUserType() {
    const previousType = this.userType;

    // PRIMEIRO: Verificar se é login da agência (Firebase Auth)
    // Agência tem prioridade máxima
    const authService = window.authService;
    
    if (authService?.currentUser && !this.forceClientMode) {
      this.userType = 'agency';
      this.clientContext = null;
      
      // Se mudou de cliente para agência, limpar sessão de cliente
      if (previousType === 'client') {
        this.clearClientSession();
      }
      
      this.notifyListeners();
      return 'agency';
    }

    // SEGUNDO: Verificar se é login do cliente (sessionStorage)
    // Só considera sessão de cliente se não há agência logada OU se está em modo forçado
    const clientAuth = sessionStorage.getItem('clientAuth');
    
    if (clientAuth) {
      try {
        const auth = JSON.parse(clientAuth);
        const now = Date.now();
        const sessionDuration = 4 * 60 * 60 * 1000; // 4 horas
        
        // Verificar se a sessão não expirou
        if ((now - auth.loginTime) < sessionDuration) {
          // Se há agência logada mas NÃO está em modo forçado, ignorar sessão de cliente
          if (authService?.currentUser && !this.forceClientMode) {
            this.clearClientSession();
          } else {
            this.userType = 'client';
            this.clientContext = auth;
            this.notifyListeners();
            return 'client';
          }
        } else {
          // Limpar sessão expirada
          this.clearClientSession();
        }
      } catch (error) {
        this.clearClientSession();
      }
    }

    // Se chegou aqui e há agência logada, retornar como agência
    if (authService?.currentUser) {
      this.userType = 'agency';
      this.clientContext = null;
      this.notifyListeners();
      return 'agency';
    }

    // Nenhum usuário autenticado
    this.userType = 'unknown';
    this.clientContext = null;
    this.notifyListeners();
    return 'unknown';
  }

  /**
   * Verifica se o usuário atual é uma agência
   */
  isAgency() {
    return this.userType === 'agency';
  }

  /**
   * Verifica se o usuário atual é um cliente
   */
  isClient() {
    return this.userType === 'client';
  }

  /**
   * Verifica se o usuário está autenticado (agência ou cliente)
   */
  isAuthenticated() {
    return this.userType === 'agency' || this.userType === 'client';
  }

  /**
   * Obtém o tipo de usuário atual (cached)
   */
  getUserType() {
    return this.userType;
  }

  /**
   * Obtém o contexto do cliente (se for login de cliente)
   */
  getClientContext() {
    if (this.isClient()) {
      return this.clientContext;
    }
    return null;
  }

  /**
   * Verifica se uma rota é permitida para o usuário atual
   */
  isRouteAllowed(path) {
    // Agência tem acesso total (mesmo em modo de visualização)
    if (this.userType === 'agency') {
      return true;
    }

    // Cliente tem acesso restrito
    if (this.userType === 'client') {
      if (!this.clientContext) return false;

      const allowedPaths = [
        `/clients/profile/${this.clientContext.clientId}`,
        `/clients/login/${this.clientContext.clientId}`
      ];

      return allowedPaths.includes(path);
    }

    // Rotas públicas
    const publicPaths = ['/', '/login', '/signup', '/privacy-policy', '/terms-of-service', '/help-center', '/documentation', '/status', '/cookie-policy', '/support', '/blog'];
    if (publicPaths.includes(path)) return true;

    // Permitir rotas de blog
    if (path.startsWith('/blog/')) return true;

    // Permitir rotas de cliente
    if (path.startsWith('/clients/login/') || path.startsWith('/clients/profile/')) {
      return true;
    }

    // Permitir padrão /:agencyId/:clientName (URL curtas para perfil de cliente)
    const pathParts = path.split('/').filter(Boolean);
    if (pathParts.length === 2 &&
        !path.startsWith('/clients/') &&
        !path.startsWith('/dashboard') &&
        !path.startsWith('/login') &&
        !path.startsWith('/settings') &&
        !path.startsWith('/upgrade') &&
        !path.startsWith('/privacy') &&
        !path.startsWith('/terms') &&
        !path.startsWith('/blog')) {
      return true; // Permite acesso, mas requireClientAccess vai redirecionar para login se necessário
    }

    return false;
  }

  /**
   * Obtém a URL de redirecionamento padrão para o usuário
   */
  getDefaultRedirectUrl() {
    if (this.userType === 'agency') {
      return '/dashboard';
    }
    
    if (this.userType === 'client' && this.clientContext) {
      return `/clients/profile/${this.clientContext.clientId}`;
    }
    
    return '/login';
  }

  /**
   * Força logout do cliente
   */
  logoutClient() {
    this.clearClientSession();
    this.forceClientMode = false;
    
    // Re-detectar tipo de usuário (pode voltar para agência se estiver logada)
    this.detectUserType();
  }

  /**
   * Limpa sessão de cliente do sessionStorage
   */
  clearClientSession() {
    sessionStorage.removeItem('clientAuth');

    // Limpar também qualquer estado de header de cliente (via AuthService)
    import('./auth.js').then(({ AuthService }) => {
      AuthService.clearClientHeaderState();
    }).catch(() => {
      // Fallback: limpar window global se import falhar
      if (window.clientHeaderState) {
        delete window.clientHeaderState;
      }
    });
  }

  /**
   * Ativa modo de visualização de cliente para agência
   * Usado quando agência quer ver a experiência do cliente
   */
  enterClientViewMode(clientId) {
    if (this.userType === 'agency') {
      this.forceClientMode = true;
    }
  }

  /**
   * Sai do modo de visualização de cliente
   */
  exitClientViewMode() {
    if (this.forceClientMode) {
      this.forceClientMode = false;
      this.clearClientSession();
      this.detectUserType();
    }
  }

  /**
   * Define contexto de impersonation (admin only)
   * @param {Object} context - {agencyId, agencyName, agencyEmail}
   */
  setImpersonationContext(context) {
    this.impersonationContext = context;
    this.notifyListeners();
  }

  /**
   * Obtém contexto de impersonation
   * @returns {Object|null}
   */
  getImpersonationContext() {
    return this.impersonationContext;
  }

  /**
   * Verifica se está em modo impersonation
   * @returns {boolean}
   */
  isImpersonating() {
    return !!this.impersonationContext?.agencyId;
  }

  /**
   * Limpa impersonation e notifica listeners
   */
  clearImpersonation() {
    this.impersonationContext = null;
    this.notifyListeners();
  }

  /**
   * Obtém agency ID efetivo (impersonated ou real)
   * @returns {string|null}
   */
  getEffectiveAgencyId() {
    if (this.isImpersonating()) {
      return this.impersonationContext.agencyId;
    }
    return window.userService?.getCurrentAgency()?.id;
  }

  /**
   * Limpa todo o contexto do usuário
   */
  clearContext() {
    this.userType = 'unknown';
    this.clientContext = null;
    this.forceClientMode = false;
    this.impersonationContext = null;
    this.clearClientSession();
    this.notifyListeners();
  }

  /**
   * Adiciona listener para mudanças no contexto do usuário
   */
  addListener(callback) {
    this.listeners.add(callback);
    
    // Retorna função para remover o listener
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notifica todos os listeners sobre mudanças
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback({
          userType: this.userType,
          clientContext: this.clientContext,
          isAgency: this.isAgency(),
          isClient: this.isClient(),
          isAuthenticated: this.isAuthenticated(),
          impersonationContext: this.impersonationContext,
          isImpersonating: this.isImpersonating()
        });
      } catch (error) {
        // silent
      }
    });
  }

  /**
   * Inicializa o serviço
   */
  initialize() {
    this.detectUserType();

    // Limpar listeners existentes antes de adicionar novos
    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler);
    }
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
    }

    // Criar handlers reutilizáveis
    this.storageHandler = (e) => {
      if (e.key === 'clientAuth') {
        // Só re-detecta se não há agência logada
        if (!window.authService?.currentUser) {
          this.detectUserType();
        }
      }
    };

    this.focusHandler = () => {
      // Re-detectar tipo ao focar na janela
      this.detectUserType();
    };

    // Escutar mudanças no storage para detectar logout de cliente
    window.addEventListener('storage', this.storageHandler);

    // Limpar estado ao mudar de aba/janela
    window.addEventListener('focus', this.focusHandler);
  }

  /**
   * Cleanup listeners
   */
  cleanup() {
    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler);
      this.storageHandler = null;
    }
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
      this.focusHandler = null;
    }
  }
}

export const userContextService = new UserContextService();